import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import SarSymbol from '@/components/SarSymbol';
import { Separator } from '@/components/ui/separator';
import type { PricingDisplayContract } from '@/lib/billing/pricingDisplay';
import { formatSarAmount } from '@/lib/canonicalMoney';

const money = (value: number) => formatSarAmount(value, 'pricing_breakdown');

export function VatIncludedLabel({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={`text-xs font-medium text-muted-foreground ${className}`}>
      {t('pricingDisplay.includingVat')}
    </span>
  );
}

export function GrossPrice({
  pricing,
  size = 'large',
  suffix,
}: {
  pricing: PricingDisplayContract;
  size?: 'small' | 'large';
  suffix?: ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline gap-1">
        <SarSymbol size={size === 'large' ? 16 : 11} />
        <span className={size === 'large'
          ? 'text-2xl font-bold text-foreground'
          : 'font-semibold text-foreground'}
        >
          {money(pricing.gross_amount_sar)}
        </span>
        {suffix}
      </div>
      <VatIncludedLabel className="mt-0.5 block" />
    </div>
  );
}

export function PricingBreakdown({
  pricing,
  className = '',
}: {
  pricing: PricingDisplayContract;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={`space-y-2 rounded-lg border border-border bg-muted/20 p-4 ${className}`}
      data-testid="pricing-breakdown"
    >
      <PricingLine
        label={t('pricingDisplay.netBeforeVat')}
        value={money(pricing.net_amount_sar)}
      />
      <PricingLine
        label={t('pricingDisplay.vatRate')}
        value={`${pricing.vat_rate_percent}%`}
        monetary={false}
      />
      <PricingLine
        label={t('pricingDisplay.vatAmount')}
        value={money(pricing.vat_amount_sar)}
      />
      <Separator />
      <PricingLine
        label={t('pricingDisplay.totalPayable')}
        value={money(pricing.gross_amount_sar)}
        emphasized
      />
      <VatIncludedLabel className="block text-end" />
    </div>
  );
}

function PricingLine({
  label,
  value,
  emphasized = false,
  monetary = true,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
  monetary?: boolean;
}) {
  return (
    <div className={`flex items-start justify-between gap-4 ${
      emphasized ? 'font-semibold' : 'text-sm'
    }`}>
      <span className="text-muted-foreground">{label}</span>
      <span className="flex shrink-0 items-center gap-1 font-mono text-foreground">
        {monetary ? <SarSymbol size={10} /> : null}
        {value}
      </span>
    </div>
  );
}
